/**
 * Attendance V2 — shared helpers for the widget + T&D page.
 * 2026-02 rebuild. Self-contained; no legacy clock code.
 */
import { requestLocation } from './geolocation';
import { formatHeaderDate } from './dateFormat';
import { toast } from 'sonner';

/** Local calendar date for the user's machine — 'YYYY-MM-DD'. */
export function localDateStr(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Acquire GPS location or explain why the action can't proceed.
 * Returns {lat,lng,accuracy,address} or null (after showing the warning).
 */
export async function getRequiredLocation(actionLabel = 'clock in') {
  const res = await requestLocation();
  if (res.ok) {
    return { lat: res.lat, lng: res.lng, accuracy: res.accuracy, address: res.address };
  }
  toast.error(
    `Location access is required to ${actionLabel}. Without it you won't be able to clock in/out. Please allow location for this site and try again.`,
    { duration: 8000 }
  );
  return null;
}

/** '2026-02-26T14:05:00+00:00' → '2:05 PM' in the viewer's timezone. */
export function fmtTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '—';
  }
}

/** Same, but rendered in a specific IANA timezone (member's local zone).
 * Falls back to the viewer's zone when tz is missing/invalid. */
export function fmtTimeInZone(iso, tz) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: 'numeric', minute: '2-digit', ...(tz ? { timeZone: tz } : {}),
    });
  } catch {
    return fmtTime(iso);
  }
}

/** 'Asia/Kolkata' → 'IST' (short zone label for row chips). */
export function tzAbbrev(tz) {
  if (!tz) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value || '';
  } catch {
    return '';
  }
}

/** '2026-07-07' (local date string) → 'Monday, July 7, 2026'. */
export function fmtDateLong(dateStr) {
  if (!dateStr) return '—';
  const [y, m, d] = String(dateStr).slice(0, 10).split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return dateStr;
  return formatHeaderDate(new Date(y, m - 1, d));
}

/** ISO → 'Monday, July 7, 2026, 7:33 PM' (viewer's timezone). */
export function fmtDateTimeLong(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${formatHeaderDate(d)}, ${fmtTime(iso)}`;
}

/** Duration of one break segment in minutes (0 when open/invalid). */
export function breakMinutes(b) {
  if (!b?.break_start_time || !b?.break_end_time) return 0;
  const a = new Date(b.break_start_time).getTime();
  const z = new Date(b.break_end_time).getTime();
  if (Number.isNaN(a) || Number.isNaN(z)) return 0;
  return Math.max(0, (z - a) / 60000);
}

/** minutes → '7h 32m' */
export function fmtMinutes(min) {
  if (min === null || min === undefined) return '—';
  const m = Math.max(0, Math.round(min));
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h ${m % 60}m` : `${m % 60}m`;
}

export const STATUS_META = {
  not_started: { label: 'Not clocked in', tone: 'muted' },
  clocked_in: { label: 'Clocked in', tone: 'green' },
  on_break: { label: 'On break', tone: 'amber' },
  clocked_out: { label: 'Clocked out', tone: 'blue' },
};
